import { memo, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Slider } from '@/sub-luca-plugin/compatible-components/index';
import { CustomAudio, ICustomAudioRef } from '@/sub-luca-plugin/compatible-components';
import { CheckCircleTwoTone } from '@/sub-luca-plugin/compatible-components/svg-icons';
import { translate_task_id, query_translate_text } from '@/sub-luca-plugin/apis';
import { QaRecordsContext } from '../../helper';
import { formatTime } from '@/sub-luca-plugin/utils';
import classNames from 'classnames';
import './index.scss';

interface IProps {
    linkContent: string; // 翻译内容
    linkTime: number; // 时间
    linkFile: string; // 音频地址
    className?: string;
}

const AudioPlayer = (props: IProps) => {
    const { linkContent, linkTime, linkFile } = props;
    const { getQaStoreByContext } = useContext(QaRecordsContext);
    const icons = useRef({
        playing: 'https://simg01.gaodunwangxiao.com/uploadfiles/files/202409/26/45414_20240926160512.png',
        paused: 'https://simg01.gaodunwangxiao.com/uploadfiles/files/202409/26/f117f_20240926160501.png',
    });
    const customAudioRef = useRef<ICustomAudioRef>();
    const [value, setValue] = useState(0);
    const [showTranslate, setShowTranslate] = useState(false);
    const [translateText, setTranslateText] = useState('');
    const [loading, setLoading] = useState(false);
    const [status, setStatus] = useState<'playing' | 'paused'>('paused');
    const [showTranslateWrapper, setTranslateWrapper] = useState(false);

    const onPlaying = () => {
        // console.log('onPlaying');
        setStatus('playing');
    };

    const onTimeupdate = (currentTime: number, duration: number) => {
        // console.log('onTimeupdate', currentTime, duration);
        setValue(currentTime);
    };

    const onPause = () => {
        setStatus('paused');
    };

    const onEnded = () => {
        setValue(0);
        setStatus('paused');
    };

    const allPause = () => {
        getQaStoreByContext()?.pauseAllAudios();
    };

    const handlePlay = () => {
        // console.log(customAudioRef.current);
        if (status === 'paused') {
            allPause();
            customAudioRef.current?.play();
        } else {
            customAudioRef.current?.pause();
        }
    };

    const drag = (v: number) => {
        allPause();
        customAudioRef.current?.seek(v);
        setValue(v);
    };

    const afterChange = () => {
        allPause();
        handlePlay();
    };

    const translate = () => {
        let time = linkTime / 10;
        time = time >= 2000 ? time : 2000;
        if (!showTranslateWrapper) {
            setShowTranslate(true);
            setTranslateWrapper(true);
            setLoading(true);
            setTimeout(() => setLoading(false), time);
        }
    };

    const retry = async () => {
        setLoading(true);
        const { result } = await translate_task_id({ fileLink: linkFile });
        if (!result?.taskId) {
            setLoading(false);
            return;
        }
        const { result: translateResult } = await query_translate_text({ taskId: result.taskId });
        if (!translateResult?.answerContent) {
            setLoading(false);
            return;
        }
        setTranslateText(translateResult.answerContent);
        setLoading(false);
    };

    useEffect(() => {
        setTranslateText(linkContent);
        getQaStoreByContext()?.addAudioRef(customAudioRef);
    }, []);

    const currentTime = useMemo(() => {
        return formatTime(value * 1000, 'mm:ss');
    }, [value]);
    const maxTime = useRef(formatTime(linkTime, 'mm:ss'));

    return (
        <div className={classNames('audio-translate', props.className)}>
            <div className="audio-wrapper" onClick={e => e.stopPropagation()}>
                <CustomAudio
                    className="my-audio"
                    src={linkFile}
                    ref={customAudioRef}
                    onPlaying={onPlaying}
                    onTimeupdate={onTimeupdate}
                    onPause={onPause}
                    onEnded={onEnded}
                />
                <div className="audio-controller">
                    <img className="audio-controller__play-btn" src={icons.current[status]} onClick={handlePlay} />
                    <div className="audio-controller-slider">
                        <Slider
                            max={linkTime / 1000}
                            step={0.01}
                            value={value}
                            onChange={(v: any) => drag(v)}
                            onAfterChange={afterChange}
                            // @ts-ignore
                            backgroundColor="rgba(150, 85, 255, 0.15)"
                            activeColor="rgba(150, 85, 255, 1)"
                        />
                        <div className="audio-time">
                            <span>{currentTime}</span>
                            <span>{maxTime.current}</span>
                        </div>
                    </div>
                    <span className="to-text" onClick={translate}>
                        转文字
                    </span>
                </div>
                {showTranslateWrapper && (
                    <>
                        {showTranslate && !loading ? (
                            <div className="translate-content">
                                {translateText ? (
                                    <>
                                        {translateText}
                                        <div className="gutter-row success-text">
                                            <CheckCircleTwoTone twoToneColor="#00d14d" className="CheckCircleTwoTone" />
                                            <span className="font-size-12" style={{ marginLeft: '4px' }}>
                                                转换完成
                                            </span>
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <div className="success-text">
                                            {!loading ? (
                                                <>
                                                    <div>转换失败</div>
                                                    <span className="font-size-12 to-text" onClick={retry}>
                                                        重试
                                                    </span>
                                                </>
                                            ) : (
                                                <>
                                                    <div style={{ display: 'block', width: '100%' }}>转换中...</div>
                                                    <div>
                                                        <img
                                                            className="text-transform-loading"
                                                            src="https://simg01.gaodunwangxiao.com/uploadfiles/files/202409/27/a7ce9_20240927095244.gif"
                                                        />
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    </>
                                )}
                            </div>
                        ) : (
                            <div className="text-center">
                                <div style={{ display: 'block', marginBottom: 10 }}>转换中...</div>
                                <div>
                                    <img
                                        className="text-transform-loading"
                                        src="https://simg01.gaodunwangxiao.com/uploadfiles/files/202409/27/a7ce9_20240927095244.gif"
                                    />
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default memo(AudioPlayer);
